import { tabKeyboard } from '@/lib/tabKeyboard';
import { useEffect, useState, type JSX, type CSSProperties } from 'react';
import {
  v2,
  ApiError,
  type Backbone,
  type LivePackageStructure,
  type LiveStructureLeaf,
} from '@/api/v2Client';
import { highlightXml } from '@/utils/highlightXml';

/**
 * Package Viewer — phase-package-viewer-live-structure (Sharon, 2026-07-24;
 * V1 parity).
 *
 * V1's package viewer was always available; V2's said "No compiled package
 * yet" until a compile existed. The STRUCTURE never needed the compile — only
 * the bytes do — so this viewer now has two sources and three states:
 *
 *   1. NO COMPILE      → the LIVE STRUCTURE: the tree of folders and files as
 *                        they would be packaged right now, derived by the SAME
 *                        functions the compiler uses (generateCanonicalFileName
 *                        + buildLeafPath server-side). Wrong placements, missing
 *                        M1 pieces and senseless operations become visible
 *                        before spending a compile.
 *   2. COMPILE, FRESH  → the compiled artifact (authoritative), as before.
 *   3. COMPILE, DRIFTED→ the artifact PLUS a prominent staleness notice and the
 *                        live structure alongside for comparison. Staleness is
 *                        the 11288d0 `computeSubmitReadiness` rule, reused —
 *                        never a second implementation.
 *
 * ## The honesty rules (the part that must not be got wrong)
 *
 * - The live view is labelled "LIVE STRUCTURE — NOT COMPILED" with a visible
 *   provisional treatment (amber banner + dashed border). It must be
 *   unmistakable for an artifact.
 * - Compile-only values (checksums, sizes, written XML) render as an explicit
 *   "pending compile" chip — NEVER a computed value, never blank-as-if-zero.
 * - NO export/download/use-this affordance exists on the live view. Only a
 *   real compiled artifact can be exported (R4/R5 stored-artifact rule).
 */

type Tab = 'tree' | 'raw';

export interface PackageViewerProps {
  submissionId: string;
}

export function PackageViewer({ submissionId }: PackageViewerProps): JSX.Element {
  const [backbone, setBackbone] = useState<Backbone | null>(null);
  const [structure, setStructure] = useState<LivePackageStructure | null>(null);
  const [tab, setTab] = useState<Tab>('tree');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setBackbone(null);
    setStructure(null);
    setError(null);
    Promise.all([
      v2.packages.getBackbone(submissionId),
      v2.packages.getLiveStructure(submissionId),
    ])
      .then(([bb, live]) => {
        setBackbone(bb);
        setStructure(live);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Failed to load package view');
      });
  }, [submissionId]);

  if (error) {
    return (
      <div role="alert" style={{ color: '#b91c1c' }}>
        {error}
      </div>
    );
  }
  if (!backbone || !structure) return <p>Loading package view…</p>;

  const hasCompiledArtifact = Boolean(backbone.backboneXml);

  // ── State 1: no compile → live structure only ────────────────────────────
  if (!hasCompiledArtifact) {
    return (
      <section aria-label="Package viewer — live structure">
        <LiveStructureView structure={structure} />
      </section>
    );
  }

  // ── States 2/3: compiled artifact (authoritative), + drift notice/live view
  // when the current documents have changed since that compile. `isStale` is
  // the 11288d0 readiness rule, computed server-side — reused, not rebuilt.
  return (
    <section aria-label="Package backbone viewer">
      {structure.isStale && (
        <div
          role="status"
          data-testid="package-drift-notice"
          style={{
            background: 'rgba(245,158,11,0.12)',
            border: '1px solid rgba(245,158,11,0.5)',
            color: '#92400e',
            borderRadius: 6,
            padding: '8px 12px',
            marginBottom: 12,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          ⚠ Documents changed since this compile — the artifact below no longer
          reflects the current submission. The live structure (further below)
          shows what a compile would produce now.
        </div>
      )}

      <div role="tablist" onKeyDown={tabKeyboard} style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'tree'} tabIndex={(tab === 'tree') ? 0 : -1}
          onClick={() => setTab('tree')}
          style={tabStyle(tab === 'tree')}
        >
          Tree
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'raw'} tabIndex={(tab === 'raw') ? 0 : -1}
          onClick={() => setTab('raw')}
          style={tabStyle(tab === 'raw')}
        >
          Raw XML
        </button>
      </div>

      {tab === 'tree' &&
        (backbone.parsedStructure ? (
          <TreeView structure={backbone.parsedStructure} />
        ) : (
          <p style={{ color: '#6b7280' }}>
            Backbone could not be parsed — switch to Raw XML to inspect.
          </p>
        ))}

      {tab === 'raw' && (
        <pre
          aria-label="Raw backbone XML"
          data-testid="backbone-xml-raw"
          style={{
            background: '#0f172a',
            color: '#e2e8f0',
            padding: 12,
            borderRadius: 6,
            overflow: 'auto',
            maxHeight: 600,
            fontSize: 12,
          }}
          // Phase 5.X-medium-batch-web — XML syntax highlighting (sanitised by highlightXml)
          dangerouslySetInnerHTML={{ __html: highlightXml(backbone.backboneXml ?? '') }}
        />
      )}

      {structure.isStale && (
        <div style={{ marginTop: 20 }}>
          <LiveStructureView structure={structure} comparison />
        </div>
      )}
    </section>
  );
}

// ── The live (derived) structure ────────────────────────────────────────────

const PROVISIONAL_LABEL = 'LIVE STRUCTURE — NOT COMPILED';

function LiveStructureView({
  structure,
  comparison = false,
}: {
  structure: LivePackageStructure;
  comparison?: boolean;
}): JSX.Element {
  // Group leaves by their folder (path minus filename) for a tree-like read.
  const byFolder = new Map<string, LiveStructureLeaf[]>();
  for (const leaf of structure.leaves) {
    const folder = leaf.path.slice(0, leaf.path.lastIndexOf('/'));
    const list = byFolder.get(folder) ?? [];
    list.push(leaf);
    byFolder.set(folder, list);
  }
  const folders = [...byFolder.keys()].sort((a, b) => a.localeCompare(b));

  return (
    <div
      data-testid="live-structure-view"
      style={{
        border: '2px dashed rgba(245,158,11,0.6)',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      {/* The provisional banner — the live view must be UNMISTAKABLE for a
          compiled artifact. */}
      <div
        data-testid="live-structure-banner"
        style={{
          background: 'rgba(245,158,11,0.14)',
          color: '#92400e',
          padding: '8px 12px',
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.05em',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span aria-hidden="true">◌</span>
        {PROVISIONAL_LABEL}
        <span style={{ fontWeight: 400, letterSpacing: 0 }}>
          — {comparison
            ? 'what a compile would produce from the current documents'
            : 'derived from the current documents; compile to produce the real package'}
        </span>
      </div>

      <div style={{ padding: 12 }}>
        <p style={{ color: '#6b7280', marginTop: 0, fontSize: 13 }}>
          Sequence folder <code>{structure.sequenceFolder}/</code> · region{' '}
          <code>{structure.region}</code> · {structure.leaves.length} file
          {structure.leaves.length === 1 ? '' : 's'}
          {structure.wouldExcludeCount > 0 && (
            <span
              data-testid="live-structure-exclusion-count"
              style={{ color: '#92400e', fontWeight: 600 }}
            >
              {' '}· {structure.wouldExcludeCount} would be excluded if compiled now
            </span>
          )}
        </p>

        {folders.length === 0 && (
          <p style={{ color: '#6b7280' }}>
            No placed documents yet — classify and place documents to see the
            structure they would form.
          </p>
        )}

        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            fontFamily: 'ui-monospace, monospace',
            fontSize: 12,
          }}
        >
          {folders.map((folder) => (
            <li key={folder} style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 700, color: 'var(--text)' }}>{folder}/</div>
              <ul style={{ listStyle: 'none', margin: 0, padding: '0 0 0 18px' }}>
                {(byFolder.get(folder) ?? []).map((leaf) => (
                  <li
                    key={leaf.documentId}
                    data-testid={`live-leaf-${leaf.documentId}`}
                    data-would-exclude={leaf.wouldBeExcluded ? 'true' : 'false'}
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 8,
                      padding: '2px 0',
                      ...(leaf.wouldBeExcluded ? { opacity: 0.65 } : null),
                    }}
                  >
                    <span>{leaf.fileName}</span>
                    <span style={opBadgeStyle}>{leaf.operation}</span>
                    {/* Compile-only values: EXPLICITLY pending. Never a number,
                        never blank-as-if-zero. */}
                    <span
                      data-testid={`live-leaf-md5-${leaf.documentId}`}
                      style={pendingChipStyle}
                    >
                      md5: pending compile
                    </span>
                    {leaf.wouldBeExcluded && (
                      <span
                        data-testid={`live-leaf-excluded-${leaf.documentId}`}
                        style={excludedChipStyle}
                        title={`A compile right now would exclude this document (${leaf.exclusionReason}).`}
                      >
                        would be excluded — {leaf.exclusionReason}
                      </span>
                    )}
                    <span style={{ color: '#9ca3af', fontSize: 11 }}>
                      ({leaf.sourceFileName})
                    </span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>

        {structure.unplaced.length > 0 && (
          <div
            data-testid="live-structure-unplaced"
            style={{ marginTop: 10, fontSize: 12, color: '#92400e' }}
          >
            <strong>
              {structure.unplaced.length} document
              {structure.unplaced.length === 1 ? '' : 's'} with no CTD placement
            </strong>{' '}
            (cannot be packaged): {structure.unplaced.map((u) => u.sourceFileName).join(', ')}
          </div>
        )}

        {/* The one thing this view must never grow: an export/download path.
            Only a real compiled artifact can leave the system. */}
        <p
          style={{
            marginTop: 12,
            marginBottom: 0,
            fontSize: 11,
            color: '#6b7280',
            fontStyle: 'italic',
          }}
        >
          Checksums, file sizes and the backbone XML are produced by a compile.
          This preview cannot be exported.
        </p>
      </div>
    </div>
  );
}

const opBadgeStyle: CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  padding: '1px 6px',
  borderRadius: 4,
  background: 'var(--surface2, #f1f5f9)',
  color: 'var(--text-muted, #64748b)',
  border: '1px solid var(--border, #e2e8f0)',
};

const pendingChipStyle: CSSProperties = {
  fontSize: 10,
  padding: '1px 6px',
  borderRadius: 4,
  background: 'rgba(245,158,11,0.10)',
  color: '#92400e',
  border: '1px dashed rgba(245,158,11,0.5)',
  fontStyle: 'italic',
};

const excludedChipStyle: CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  padding: '1px 6px',
  borderRadius: 4,
  background: 'rgba(239,68,68,0.10)',
  color: '#b91c1c',
  border: '1px solid rgba(239,68,68,0.4)',
};

function TreeView({
  structure,
}: {
  structure: NonNullable<Backbone['parsedStructure']>;
}): JSX.Element {
  return (
    <div>
      <p style={{ color: '#6b7280', marginTop: 0 }}>
        Sequence {structure.sequenceNumber} · {structure.region}
      </p>
      {structure.modules.length === 0 && (
        <p style={{ color: '#6b7280' }}>No modules found in backbone.</p>
      )}
      <ul style={{ paddingLeft: 16 }}>
        {structure.modules.map((m) => (
          <li key={m.moduleNumber} style={{ marginBottom: 8 }}>
            <strong>Module {m.moduleNumber}</strong>
            <ul style={{ paddingLeft: 16 }}>
              {m.sections.map((s) => (
                <li key={s.sectionCode}>
                  {s.sectionCode} — {s.title}{' '}
                  <span style={{ color: '#6b7280' }}>({s.leafCount} leaves)</span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}

function tabStyle(active: boolean): CSSProperties {
  return {
    padding: '6px 12px',
    border: '1px solid #d1d5db',
    background: active ? '#1B3A6B' : '#fff',
    color: active ? '#fff' : '#1B3A6B',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 600,
  };
}
